/**
 * Centralized HTML entity decoder for the entire application
 * 
 * This utility consolidates HTML entity decoding logic that was previously
 * duplicated across multiple services (yDocLLMAPI, yDocLowLevelAPI, yDocHighLevelAPI).
 * 
 * Usage:
 *   const decoded = HtmlEntityDecoder.decode(text, 'context_name');
 */
class HtmlEntityDecoder {
  private static readonly ENTITY_MAP = {
    // Basic HTML entities (originally covered)
    '&quot;': '"',
    '&#34;': '"',     // Numeric version of &quot;
    '&#39;': "'",
    '&#x27;': "'",    // Hex version of &#39;
    '&apos;': "'", 
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&nbsp;': ' ',
    '&#160;': ' ',    // Numeric version of &nbsp;
    
    // Typography entities
    '&mdash;': '—',
    '&ndash;': '–',
    '&hellip;': '…',
    '&lsquo;': '\u2018',  // Left single quote
    '&rsquo;': '\u2019',  // Right single quote
    '&ldquo;': '\u201C',  // Left double quote
    '&rdquo;': '\u201D',  // Right double quote
    
    // Mathematical symbols
    '&divide;': '÷',
    '&times;': '×',
    '&plusmn;': '±',
    '&minus;': '−',
    '&deg;': '°',
    '&sup2;': '²',
    '&sup3;': '³',
    '&frac12;': '½',
    '&frac14;': '¼',
    '&frac34;': '¾',
    
    // Currency symbols
    '&euro;': '€',
    '&pound;': '£',
    '&yen;': '¥',
    '&cent;': '¢',
    
    // Accented characters (common Latin)
    '&eacute;': 'é',
    '&egrave;': 'è',
    '&ecirc;': 'ê',
    '&euml;': 'ë',
    '&aacute;': 'á',
    '&agrave;': 'à',
    '&acirc;': 'â',
    '&auml;': 'ä',
    '&iacute;': 'í',
    '&igrave;': 'ì',
    '&icirc;': 'î',
    '&iuml;': 'ï',
    '&oacute;': 'ó',
    '&ograve;': 'ò',
    '&ocirc;': 'ô',
    '&ouml;': 'ö',
    '&uacute;': 'ú',
    '&ugrave;': 'ù',
    '&ucirc;': 'û',
    '&uuml;': 'ü',
    '&ntilde;': 'ñ',
    '&ccedil;': 'ç',
    
    // Arrows
    '&larr;': '←',
    '&rarr;': '→',
    '&uarr;': '↑',
    '&darr;': '↓',
    '&harr;': '↔',
    
    // Greek letters (common)
    '&alpha;': 'α',
    '&beta;': 'β',
    '&gamma;': 'γ',
    '&delta;': 'δ',
    '&epsilon;': 'ε',
    '&zeta;': 'ζ',
    '&eta;': 'η',
    '&theta;': 'θ',
    '&iota;': 'ι',
    '&kappa;': 'κ',
    '&lambda;': 'λ',
    '&mu;': 'μ',
    '&nu;': 'ν',
    '&xi;': 'ξ',
    '&omicron;': 'ο',
    '&pi;': 'π',
    '&rho;': 'ρ',
    '&sigma;': 'σ',
    '&tau;': 'τ',
    '&upsilon;': 'υ',
    '&phi;': 'φ',
    '&chi;': 'χ',
    '&psi;': 'ψ',
    '&omega;': 'ω',
    
    // Symbols and special characters
    '&copy;': '©',
    '&reg;': '®',
    '&trade;': '™',
    '&hearts;': '♥',
    '&spades;': '♠',
    '&clubs;': '♣',
    '&diams;': '♦',
    '&bull;': '•',
    '&sect;': '§',
    '&para;': '¶',
    '&dagger;': '†',
    '&Dagger;': '‡',
    '&permil;': '‰'
  };

  /**
   * Decode HTML entities to Unicode characters
   * 
   * @param input - The text that may contain HTML entities
   * @param context - Optional context for debugging logs
   * @returns Decoded text with Unicode characters
   */
  static decode(input: string | undefined | null, context?: string): string {
    if (!input) return '';
    let decoded = String(input);
    
    // 🔍 PRE-PROCESSING QUOTE DEBUG
    const hasQuoteEntities = /&quot;|&#34;|&#39;|&#x27;|&apos;/.test(decoded);
    if (hasQuoteEntities && context) {
      console.log(`🔍 [SHARED_DECODE_QUOTE] ${context} - Input contains quote entities:`, {
        input: decoded.substring(0, 150),
        quotEntities: decoded.match(/&quot;/g) || [],
        num34Entities: decoded.match(/&#34;/g) || [],
        apos39Entities: decoded.match(/&#39;/g) || [],
        hexAposEntities: decoded.match(/&#x27;/g) || []
      });
    }
    
    // Step 1a: EXPLICIT QUOTE HANDLING FIRST (most critical)
    const quoteBefore = (decoded.match(/&quot;/g) || []).length;
    const aposBefore = (decoded.match(/&#39;/g) || []).length;
    
    decoded = decoded
      .replace(/&quot;/g, '"')
      .replace(/&#34;/g, '"')
      .replace(/&#x22;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#x27;/g, "'")
      .replace(/&apos;/g, "'");
    
    const quotAfter = (decoded.match(/&quot;/g) || []).length;
    const aposAfter = (decoded.match(/&#39;/g) || []).length;
    
    if (quoteBefore > 0 || aposBefore > 0) {
      console.log(`🔍 [EXPLICIT_QUOTES] Quote fixes: &quot; ${quoteBefore}→${quotAfter}, &#39; ${aposBefore}→${aposAfter}`);
    }
    
    // Step 1b: Apply other named entity replacements  
    for (const [entity, char] of Object.entries(this.ENTITY_MAP)) {
      // Skip quotes since we handled them above
      if (['&quot;', '&#34;', '&#x22;', '&#39;', '&#x27;', '&apos;'].includes(entity)) continue;
      
      decoded = decoded.replace(new RegExp(entity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), char);
    }
    
    // Step 2: Decode numeric entities (decimal)
    decoded = decoded.replace(/&#(\d+);/g, (_, dec) => {
      const code = parseInt(dec, 10);
      return Number.isFinite(code) ? String.fromCharCode(code) : _;
    });
    
    // Step 3: Decode numeric entities (hexadecimal)  
    decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => {
      const code = parseInt(hex, 16);
      return Number.isFinite(code) ? String.fromCharCode(code) : _;
    });
    
    // Step 4: 🛡️ FALLBACK - Use browser's native HTML parser for any remaining entities
    // This catches any HTML entities we might have missed in our ENTITY_MAP
    if (typeof document !== 'undefined' && /&[a-zA-Z][a-zA-Z0-9]*;/.test(decoded)) {
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = decoded;
        const fallbackDecoded = tempDiv.textContent || tempDiv.innerText || decoded;
        if (fallbackDecoded !== decoded) {
          console.log(`🔧 [HTML_DECODE_FALLBACK] ${context || 'Unknown'}: Browser decoded additional entities`);
          decoded = fallbackDecoded;
        }
      } catch (error) {
        // If DOM manipulation fails, continue with our manual decoding
        console.warn('[HTML_DECODE] DOM fallback failed, using manual decoding only:', error);
      }
    }
    
    // Step 5: FINAL QUOTE SAFETY NET (just to be absolutely sure)
    decoded = decoded
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
      
    // 🔍 POST-PROCESSING QUOTE DEBUG
    if (hasQuoteEntities && context) {
      console.log(`🔍 [SHARED_DECODE_FINAL] ${context} - Final quote processing results:`, {
        stillHasQuot: decoded.includes('&quot;'),
        stillHasNum34: decoded.includes('&#34;'),
        stillHasApos39: decoded.includes('&#39;'),
        stillHasHexApos: decoded.includes('&#x27;'),
        finalResult: decoded.substring(0, 150),
        quotesSuccessfullyDecoded: input.includes('&quot;') && !decoded.includes('&quot;')
      });
    }
    
    // Optional logging for debugging (only when entities were actually found)
    if (decoded !== input && context) {
      console.log(`🔧 [HTML_DECODE] ${context}:`, {
        original: input.substring(0, 100) + (input.length > 100 ? '...' : ''),
        decoded: decoded.substring(0, 100) + (decoded.length > 100 ? '...' : ''),
        entitiesFound: input.match(/&[a-zA-Z0-9#]+;/g) || [],
        hasEntities: true
      });
    }
    
    return decoded;
  }

  /**
   * Check if text contains HTML entities (for optimization)
   */
  static hasEntities(input: string | undefined | null): boolean {
    if (!input) return false;
    return /&[a-zA-Z0-9#]+;/.test(input);
  }
}

// ES Module export
export { HtmlEntityDecoder };

// CommonJS export for compatibility with require()
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { HtmlEntityDecoder };
}
